import React, { useEffect, useRef, useState } from 'react';
import { ExternalLink, FileText, Link2, Mail, Receipt as ReceiptIcon, Route, Trash2 } from 'lucide-react';
import { ReceiptUpload } from '../components/ReceiptUpload';
import {
  useReceipts,
  useAddReceipt,
  useRemoveReceipt,
  useResyncReceiptRoutes,
  useUpdateReceiptCategory,
  useUpdateReceipt,
} from '../hooks/useTravelBuddy';
import { getReceiptBlob } from '../lib/receiptBlobStore';
import { RECEIPT_CATEGORY_LABELS, RECEIPT_CATEGORY_OPTIONS } from '../lib/categoryLabels';
import { getReceiptContactsForDisplay, websiteHref } from '../lib/contactExtraction';
import { isTravelDocumentReceipt } from '../lib/receiptBuckets';
import type { Receipt, ReceiptCategory } from '../types';
import * as T from '../ui/inlineTheme';
import { sf } from '../../themeConfig/sisuForgeTokens';

const emptyBox: React.CSSProperties = {
  ...T.cardSolid,
  textAlign: 'center',
  borderStyle: 'dashed',
  color: sf.textMuted,
  fontFamily: T.font,
  fontSize: 15,
  lineHeight: 1.5,
};

const removeReceiptBtn: React.CSSProperties = {
  padding: 8,
  border: 'none',
  borderRadius: 8,
  background: 'transparent',
  color: sf.textMuted,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  alignSelf: 'flex-start',
  transition: 'color 0.15s ease, background-color 0.15s ease',
};

const receiptRow: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 14,
  overflow: 'hidden',
  borderRadius: 12,
  border: `1px solid ${sf.border}`,
  backgroundColor: sf.surfaceElevated,
  padding: 14,
  boxSizing: 'border-box',
};

const thumb: React.CSSProperties = {
  width: 68,
  height: 68,
  flexShrink: 0,
  borderRadius: 10,
  backgroundColor: sf.surface,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
};

const statusLine: React.CSSProperties = {
  margin: 0,
  padding: '12px 14px',
  borderRadius: 10,
  backgroundColor: 'rgba(58, 141, 255, 0.1)',
  border: '1px solid rgba(58, 141, 255, 0.35)',
  fontFamily: T.font,
  fontSize: 14,
  color: sf.accent,
  lineHeight: 1.45,
};

const sectionTitle: React.CSSProperties = {
  ...T.labelSmall,
  marginBottom: 6,
};

const sectionHint: React.CSSProperties = {
  margin: '0 0 10px',
  fontFamily: T.font,
  fontSize: 14,
  color: sf.textMuted,
  lineHeight: 1.45,
};

const switchRow: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 14,
  marginTop: 10,
  alignItems: 'center',
};

const switchLabel: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  fontFamily: T.font,
  fontSize: 13,
  color: '#d4d4d8',
  cursor: 'pointer',
  userSelect: 'none',
};

const fieldLabel: React.CSSProperties = {
  fontFamily: T.font,
  fontSize: 12,
  color: '#a1a1aa',
  marginBottom: 4,
};

const textInput: React.CSSProperties = {
  ...T.select,
  width: '100%',
  maxWidth: 320,
  fontSize: 14,
};

/** Receipt title line — looks like the old static heading, editable */
const headingInput: React.CSSProperties = {
  ...T.select,
  display: 'block',
  width: '100%',
  maxWidth: '100%',
  margin: 0,
  padding: '4px 6px',
  marginLeft: -6,
  fontFamily: T.font,
  fontSize: 17,
  fontWeight: 600,
  color: '#fafafa',
  backgroundColor: 'transparent',
  border: '1px solid transparent',
  borderRadius: 8,
  boxSizing: 'border-box',
};

const smallInput: React.CSSProperties = {
  ...T.select,
  fontSize: 14,
  maxWidth: 120,
};

function ReceiptThumb({ receipt }: { receipt: Receipt }) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const urlRef = useRef<string | null>(null);
  const isPdf =
    receipt.mime_type === 'application/pdf' || receipt.file_name?.toLowerCase().endsWith('.pdf');

  useEffect(() => {
    const revoke = () => {
      if (urlRef.current) {
        URL.revokeObjectURL(urlRef.current);
        urlRef.current = null;
      }
    };
    if (receipt.image_url || isPdf) {
      revoke();
      setBlobUrl(null);
      return;
    }
    let cancelled = false;
    void (async () => {
      const blob = await getReceiptBlob(receipt.id);
      if (cancelled || !blob?.type.startsWith('image/')) return;
      const next = URL.createObjectURL(blob);
      if (cancelled) {
        URL.revokeObjectURL(next);
        return;
      }
      revoke();
      urlRef.current = next;
      setBlobUrl(next);
    })();
    return () => {
      cancelled = true;
      revoke();
    };
  }, [receipt.id, receipt.image_url, isPdf]);

  const src = receipt.image_url || blobUrl;
  if (src && !isPdf) {
    return (
      <img src={src} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
    );
  }
  if (isPdf) {
    return <FileText size={32} color={sf.textMuted} aria-hidden />;
  }
  return <ReceiptIcon size={28} color={sf.textMuted} />;
}

async function openReceiptFile(r: Receipt): Promise<void> {
  const blob = await getReceiptBlob(r.id);
  if (blob && blob.size > 0) {
    const url = URL.createObjectURL(blob);
    const win = window.open(url, '_blank', 'noopener,noreferrer');
    if (!win) {
      URL.revokeObjectURL(url);
      window.alert('Pop-up blocked. Allow pop-ups for this site to view the file in a new tab.');
      return;
    }
    window.setTimeout(() => URL.revokeObjectURL(url), 120_000);
    return;
  }
  if (r.image_url) {
    window.open(r.image_url, '_blank', 'noopener,noreferrer');
    return;
  }
  window.alert('No file is stored for this receipt in this browser.');
}

type RowProps = {
  r: Receipt;
  busy: boolean;
  resyncRoutes: ReturnType<typeof useResyncReceiptRoutes>;
  remove: ReturnType<typeof useRemoveReceipt>;
  updateCategory: ReturnType<typeof useUpdateReceiptCategory>;
  updateReceipt: ReturnType<typeof useUpdateReceipt>;
  showRouteResync: boolean;
  setOpeningId: (id: string | null) => void;
  setResyncStatus: (s: string) => void;
};

function ReceiptListRow({
  r,
  busy,
  resyncRoutes,
  remove,
  updateCategory,
  updateReceipt,
  showRouteResync,
  setOpeningId,
  setResyncStatus,
}: RowProps) {
  const [merchant, setMerchant] = useState(r.merchant ?? '');
  const [amountEur, setAmountEur] = useState(r.amount_eur ?? '');
  const [amountRaw, setAmountRaw] = useState(r.amount);
  const [currencyRaw, setCurrencyRaw] = useState(r.currency);

  useEffect(() => {
    setMerchant(r.merchant ?? '');
    setAmountEur(r.amount_eur ?? '');
    setAmountRaw(r.amount);
    setCurrencyRaw(r.currency);
  }, [r.id, r.merchant, r.amount_eur, r.amount, r.currency]);

  const flushEdits = () => {
    updateReceipt.mutate({
      receiptId: r.id,
      patch: {
        merchant: merchant.trim(),
        // file_name: fileName,
        amount: amountRaw,
        currency: currencyRaw,
        amount_eur: amountEur.trim() === '' ? null : amountEur.trim(),
      },
    });
  };

  const disabledRow =
    busy || updateReceipt.isPending || resyncRoutes.isPending || remove.isPending;

  return (
    <li style={receiptRow}>
      <button
        type="button"
        onClick={() => {
          setOpeningId(r.id);
          void openReceiptFile(r).finally(() => setOpeningId(null));
        }}
        disabled={disabledRow}
        title="Open file in new tab"
        aria-label={`Open receipt file${r.file_name ? `: ${r.file_name}` : ''}`}
        style={{
          ...thumb,
          border: 'none',
          padding: 0,
          cursor: busy ? 'wait' : 'pointer',
          opacity: busy ? 0.75 : 1,
        }}
      >
        <ReceiptThumb receipt={r} />
      </button>
      <div style={{ minWidth: 0, flex: 1 }}>
        <input
          type="text"
          style={headingInput}
          value={merchant}
          onChange={(e) => setMerchant(e.target.value)}
          onBlur={flushEdits}
          disabled={disabledRow}
          placeholder="Merchant or document name"
          autoComplete="off"
          aria-label="Receipt title"
        />
        <div style={switchRow}>
          <label style={switchLabel}>
            <input
              type="checkbox"
              role="switch"
              checked={r.include_in_receipts_pdf !== false}
              onChange={(e) =>
                updateReceipt.mutate({
                  receiptId: r.id,
                  patch: { include_in_receipts_pdf: e.target.checked },
                })
              }
              disabled={disabledRow}
              style={{ width: 18, height: 18, cursor: disabledRow ? 'wait' : 'pointer', accentColor: '#3A8DFF' }}
            />
            Receipts PDF
          </label>
          <label style={switchLabel}>
            <input
              type="checkbox"
              role="switch"
              checked={r.include_in_summary !== false}
              onChange={(e) =>
                updateReceipt.mutate({
                  receiptId: r.id,
                  patch: { include_in_summary: e.target.checked },
                })
              }
              disabled={disabledRow}
              style={{ width: 18, height: 18, cursor: disabledRow ? 'wait' : 'pointer', accentColor: '#22c55e' }}
            />
            Summary
          </label>
        </div>
        {/* File name editing disabled — title field above replaces labeling; blob name unchanged
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 8, maxWidth: 360 }}>
          <span style={fieldLabel}>File name</span>
          <input
            type="text"
            style={textInput}
            value={fileName}
            onChange={(e) => setFileName(e.target.value)}
            onBlur={flushEdits}
            disabled={disabledRow}
            placeholder="e.g. hotel-bill.pdf"
            autoComplete="off"
          />
        </label>
        */}
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 12,
            marginTop: 10,
            alignItems: 'flex-end',
          }}
        >
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
            <span style={fieldLabel}>Amount (EUR) — summary &amp; PDF</span>
            <input
              type="text"
              inputMode="decimal"
              style={{ ...smallInput, maxWidth: 140 }}
              value={amountEur}
              onChange={(e) => setAmountEur(e.target.value)}
              onBlur={flushEdits}
              disabled={disabledRow}
              placeholder={currencyRaw.toUpperCase() === 'EUR' ? amountRaw : 'e.g. 42.50'}
              title="Overrides EUR total when set. Leave empty to use original amount if currency is EUR."
              autoComplete="off"
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
            <span style={fieldLabel}>Original amount</span>
            <input
              type="text"
              inputMode="decimal"
              style={{ ...smallInput, maxWidth: 110 }}
              value={amountRaw}
              onChange={(e) => setAmountRaw(e.target.value)}
              onBlur={flushEdits}
              disabled={disabledRow}
              autoComplete="off"
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
            <span style={fieldLabel}>Currency</span>
            <input
              type="text"
              style={{ ...smallInput, maxWidth: 72 }}
              value={currencyRaw}
              onChange={(e) => setCurrencyRaw(e.target.value.toUpperCase())}
              onBlur={flushEdits}
              disabled={disabledRow}
              maxLength={8}
              autoComplete="off"
            />
          </label>
        </div>
        <p style={{ margin: '6px 0 0', fontFamily: T.font, fontSize: 12, color: '#71717a', lineHeight: 1.4 }}>
          Non-EUR receipts: enter <strong style={{ color: '#a1a1aa' }}>Amount (EUR)</strong> so totals match your
          reporting currency.
        </p>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 8, maxWidth: 280 }}>
          <span style={{ fontFamily: T.font, fontSize: 12, color: '#a1a1aa' }}>Category</span>
          <select
            style={T.select}
            value={r.category}
            onChange={(e) =>
              updateCategory.mutate({
                receiptId: r.id,
                category: e.target.value as ReceiptCategory,
              })
            }
            disabled={disabledRow}
          >
            {RECEIPT_CATEGORY_OPTIONS.map((c) => (
              <option key={c} value={c}>
                {RECEIPT_CATEGORY_LABELS[c]}
              </option>
            ))}
          </select>
        </label>
        <p style={{ margin: '8px 0 0', fontFamily: T.font, fontSize: 13, color: '#9CA3AF' }}>
          {r.date}
          {r.file_name ? ` · ${r.file_name}` : ''}
        </p>
        {(() => {
          const { emails, websites } = getReceiptContactsForDisplay(r);
          if (emails.length === 0 && websites.length === 0) return null;
          const linkStyle: React.CSSProperties = {
            color: '#67e8f9',
            fontSize: 13,
            wordBreak: 'break-all',
            textDecoration: 'none',
          };
          return (
            <div style={{ marginTop: 10 }}>
              <p style={{ fontFamily: T.font, fontSize: 12, color: '#a1a1aa', margin: '0 0 6px' }}>
                Contacts from document
              </p>
              <ul
                style={{
                  listStyle: 'none',
                  margin: 0,
                  padding: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 6,
                }}
              >
                {emails.map((e) => (
                  <li key={`e:${e}`}>
                    <a
                      href={`mailto:${e}`}
                      style={{ ...linkStyle, display: 'inline-flex', alignItems: 'center', gap: 6 }}
                    >
                      <Mail size={14} aria-hidden />
                      {e}
                    </a>
                  </li>
                ))}
                {websites.map((w) => (
                  <li key={`w:${w}`}>
                    <a
                      href={websiteHref(w)}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ ...linkStyle, display: 'inline-flex', alignItems: 'center', gap: 6 }}
                    >
                      <Link2 size={14} aria-hidden />
                      {w}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          );
        })()}
        {r.analysis_note && (
          <p style={{ margin: '8px 0 0', fontFamily: T.font, fontSize: 12, color: '#eab308', lineHeight: 1.4 }}>
            {r.analysis_note}
          </p>
        )}
        {r.raw_text_excerpt && (
          <details style={{ marginTop: 8 }}>
            <summary style={{ fontFamily: T.font, fontSize: 12, color: '#9CA3AF', cursor: 'pointer' }}>
              Extracted text excerpt
            </summary>
            <p
              style={{
                margin: '8px 0 0',
                fontFamily: 'ui-monospace, Menlo, monospace',
                fontSize: 11,
                color: '#a1a1aa',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                maxHeight: 120,
                overflow: 'auto',
              }}
            >
              {r.raw_text_excerpt}
            </p>
          </details>
        )}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flexShrink: 0 }}>
        <button
          type="button"
          data-tb-icon="true"
          title="Open file in new tab"
          style={{
            ...removeReceiptBtn,
            opacity: disabledRow ? 0.45 : 1,
            cursor: disabledRow ? 'wait' : 'pointer',
            color: '#a5b4fc',
          }}
          aria-label={`Open receipt file ${r.file_name || r.merchant}`}
          disabled={disabledRow}
          onClick={() => {
            setOpeningId(r.id);
            void openReceiptFile(r).finally(() => setOpeningId(null));
          }}
        >
          <ExternalLink size={18} strokeWidth={2} />
        </button>
        {showRouteResync && (
          <button
            type="button"
            data-tb-icon="true"
            title="Re-scan file for flight routes (uses OCR on scanned PDFs)"
            style={{
              ...removeReceiptBtn,
              opacity: disabledRow ? 0.45 : 1,
              cursor: disabledRow ? 'wait' : 'pointer',
              color: '#67e8f9',
            }}
            aria-label={`Re-scan routes for ${r.merchant}`}
            disabled={disabledRow}
            onClick={() => {
              setResyncStatus('');
              resyncRoutes.mutate(
                {
                  receiptId: r.id,
                  onProgress: (msg: string) => setResyncStatus(msg),
                },
                {
                  onSettled: () => setResyncStatus(''),
                }
              );
            }}
          >
            <Route size={18} strokeWidth={2} />
          </button>
        )}
        <button
          type="button"
          data-tb-icon="true"
          style={{
            ...removeReceiptBtn,
            opacity: disabledRow ? 0.45 : 1,
            cursor: disabledRow ? 'wait' : 'pointer',
          }}
          aria-label={`Delete receipt ${r.merchant}`}
          disabled={disabledRow}
          onClick={() => remove.mutate(r.id)}
        >
          <Trash2 size={18} strokeWidth={2} />
        </button>
      </div>
    </li>
  );
}

export function ReceiptsTab({ tripId }: { tripId: string }) {
  const { data: receipts = [], isLoading } = useReceipts(tripId);
  const add = useAddReceipt();
  const remove = useRemoveReceipt(tripId);
  const resyncRoutes = useResyncReceiptRoutes(tripId);
  const updateCategory = useUpdateReceiptCategory(tripId);
  const updateReceipt = useUpdateReceipt(tripId);
  const [analyzeStatus, setAnalyzeStatus] = useState('');
  const [resyncStatus, setResyncStatus] = useState('');
  const [openingId, setOpeningId] = useState<string | null>(null);

  const travelDocs = receipts.filter(isTravelDocumentReceipt);
  const expenseReceipts = receipts.filter((r) => !isTravelDocumentReceipt(r));

  const onFile = (file: File) => {
    setAnalyzeStatus('Starting…');
    add.mutate(
      {
        tripId,
        file,
        onProgress: (msg) => setAnalyzeStatus(msg),
      },
      {
        onSettled: () => setAnalyzeStatus(''),
      }
    );
  };

  const rowProps = {
    resyncRoutes,
    remove,
    updateCategory,
    updateReceipt,
    setOpeningId,
    setResyncStatus,
  };

  return (
    <div style={T.flexGap}>
      <ReceiptUpload disabled={add.isPending} onFile={onFile} />
      <p style={{ ...T.bodyMuted, margin: 0 }}>
        Upload any file once; we guess the category and list it under{' '}
        <strong style={{ color: '#d4d4d8' }}>Travel documents</strong> (flights, trains, buses, ferries, taxi/ride)
        or <strong style={{ color: '#d4d4d8' }}>Receipts</strong> (food, hotels, and other expenses).
      </p>
      {(add.isPending || analyzeStatus || resyncRoutes.isPending || resyncStatus) && (
        <p style={statusLine}>
          {add.isPending
            ? analyzeStatus || 'Analyzing receipt…'
            : resyncRoutes.isPending
              ? resyncStatus || 'Re-scanning for routes…'
              : analyzeStatus || resyncStatus}
        </p>
      )}
      {add.isError && (
        <p style={{ margin: 0, fontFamily: T.font, fontSize: 15, color: '#f87171' }}>
          Could not add receipt. Try again.
        </p>
      )}
      {resyncRoutes.isError && (
        <p style={{ margin: 0, fontFamily: T.font, fontSize: 15, color: '#f87171' }}>
          Could not re-scan routes.
        </p>
      )}

      {isLoading && <p style={{ ...T.bodyMuted, margin: 0 }}>Loading…</p>}

      {!isLoading && (
        <>
          <div>
            <h3 style={sectionTitle}>Travel documents</h3>
            <p style={sectionHint}>
              Boarding passes, e-tickets, train and bus tickets, ferries, and taxi or ride receipts. Use the route
              icon to re-scan for flights when needed.
            </p>
            {travelDocs.length === 0 && !add.isPending && (
              <p style={emptyBox}>
                No travel tickets yet. When you upload boarding passes or similar PDFs, they appear here once
                categorized as flight, train, bus, ferry, or transport.
              </p>
            )}
            {travelDocs.length > 0 && (
              <ul
                style={{
                  listStyle: 'none',
                  margin: '12px 0 0',
                  padding: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 12,
                }}
              >
                {travelDocs.map((r) => (
                  <ReceiptListRow
                    key={r.id}
                    r={r}
                    busy={openingId === r.id}
                    showRouteResync
                    {...rowProps}
                  />
                ))}
              </ul>
            )}
          </div>

          <div style={{ marginTop: 24 }}>
            <h3 style={sectionTitle}>Receipts</h3>
            <p style={sectionHint}>Food, lodging, shopping, and other trip expenses — not tickets or transport passes.</p>
            {expenseReceipts.length === 0 && !add.isPending && (
              <p style={emptyBox}>
                No expense receipts yet. Restaurant bills, hotel folios, and similar files show here when categorized
                as food, lodging, or other.
              </p>
            )}
            {expenseReceipts.length > 0 && (
              <ul
                style={{
                  listStyle: 'none',
                  margin: '12px 0 0',
                  padding: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 12,
                }}
              >
                {expenseReceipts.map((r) => (
                  <ReceiptListRow
                    key={r.id}
                    r={r}
                    busy={openingId === r.id}
                    showRouteResync={false}
                    {...rowProps}
                  />
                ))}
              </ul>
            )}
          </div>
        </>
      )}
    </div>
  );
}
